export type ColorTheme = 'crema' | 'lino' | 'carbon' | 'tinta' | 'slate' | 'ocean' | 'forest' | 'stone';
export type DisplayFont =
  | 'cormorant'
  | 'playfair'
  | 'marcellus'
  | 'italiana'
  | 'tenor'
  | 'forum'
  | 'fraunces'
  | 'bricolage'
  | 'prata'
  | 'libre-baskerville'
  | 'lora'
  | 'sora'
  | 'space-grotesk'
  | 'montserrat'
  | 'archivo';
export type BodyFont =
  | 'dm-sans'
  | 'manrope'
  | 'albert-sans'
  | 'inter-tight'
  | 'inter'
  | 'outfit'
  | 'plus-jakarta'
  | 'source-sans'
  | 'work-sans'
  | 'instrument-sans'
  | 'public-sans'
  | 'nunito-sans'
  | 'nunito'
  | 'karla';
export type Density = 'compact' | 'regular' | 'spacious';
export type FontScale = 'sm' | 'md' | 'lg' | 'xl';

/** Conversión px↔rem para el tamaño de fuente base (raíz = 16 px). La UI muestra px;
 *  el dato se persiste en rem. */
export const PX_PER_REM = 16;
/** Límites del tamaño de fuente base, en rem (≈ 13 px – 22 px). */
export const FONT_BASE_REM_MIN = 0.8125;
export const FONT_BASE_REM_MAX = 1.375;
export const FONT_BASE_REM_DEFAULT = 1; // 16 px

/** Normaliza un valor de tamaño base en rem: clampa al rango y redondea a px enteros.
 *  Devuelve `undefined` si no es un número finito (→ se usa el enum `fontScale`). */
export function sanitizeFontBaseRem(value: unknown): number | undefined {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  const clamped = Math.min(FONT_BASE_REM_MAX, Math.max(FONT_BASE_REM_MIN, n));
  // Redondea a px enteros para que la UI (px) y el dato (rem) no acumulen decimales.
  return Math.round(clamped * PX_PER_REM) / PX_PER_REM;
}
export type CardStyle = 'shadow' | 'frame' | 'minimal';
export type CornerRadius = 'none' | 'subtle' | 'rounded';
export type CardBorderColor = 'gold' | 'accent' | 'ink' | 'soft';
export type CardBorderWidth = 'thin' | 'regular' | 'thick';
/** Acabado de las superficies/tarjetas. 'glass' = translúcido + desenfoque. */
export type SurfaceStyle = 'solid' | 'glass';
/** Fondo de página. 'mesh' = degradado animado + ruido (look glassmorphism). */
export type BackgroundStyle = 'plain' | 'mesh';
/** Intensidad del desenfoque del glass (solo aplica con surfaceStyle 'glass'). */
export type GlassBlur = 'subtle' | 'regular' | 'strong';
export type BrandingSource = 'industry-suggestion' | 'ai-refined' | 'user-custom';

export interface BrandingTheme {
  colorTheme?: ColorTheme;
  accent?: string;
  /** Color secundario / brillo. Alimenta el segundo foco del fondo mesh.
   *  Si no se define, se deriva del tema (deepGold). */
  accent2?: string;
  /** Tercer color de marca / highlight. Opcional: si no se define no se emite
   *  el token --p-accent3 y el tercer foco del mesh deriva del accent. */
  accent3?: string;
  deepAlt?: string;
  fontDisplay?: DisplayFont;
  fontBody?: BodyFont;
  density?: Density;
  fontScale?: FontScale;
  /** Tamaño de fuente base, almacenado en **rem** (1 rem = 16 px). Opcional.
   *  La UI lo edita en píxeles (px = rem × 16) pero internamente se persiste en rem.
   *  Si se define, gobierna `--fs-base` (multiplicador sobre los clamps en rem) y tiene
   *  prioridad sobre `fontScale`; si falta, `--fs-base` deriva del enum `fontScale`. */
  fontBaseRem?: number;
  cardStyle?: CardStyle;
  cornerRadius?: CornerRadius;
  cardBorderColor?: CardBorderColor;
  cardBorderWidth?: CardBorderWidth;
  surfaceStyle?: SurfaceStyle;
  backgroundStyle?: BackgroundStyle;
  glassBlur?: GlassBlur;
  brandingSource?: BrandingSource;
  brandingIndustry?: string;
  brandingLabel?: string;
  brandingReason?: string;
}

export interface BrandingOption<T extends string> {
  value: T;
  label: string;
  family?: string;
  provider?: string;
  category?: string;
}

export interface BrandingSuggestion {
  key: string;
  industryKey: string | null;
  label: string;
  paletteName: string;
  rationale: string;
  theme: BrandingTheme;
}

const PROVIDER = 'Google Fonts';

export const COLOR_THEME_OPTIONS: BrandingOption<ColorTheme>[] = [
  { value: 'crema', label: 'Crema', category: 'Palacete' },
  { value: 'lino', label: 'Lino', category: 'Palacete' },
  { value: 'carbon', label: 'Carbón', category: 'Palacete' },
  { value: 'tinta', label: 'Tinta', category: 'Palacete' },
  { value: 'slate', label: 'Slate', category: 'Genérico' },
  { value: 'ocean', label: 'Ocean', category: 'Genérico' },
  { value: 'forest', label: 'Forest', category: 'Genérico' },
  { value: 'stone', label: 'Stone', category: 'Genérico' },
];

export const DISPLAY_FONT_OPTIONS: BrandingOption<DisplayFont>[] = [
  { value: 'cormorant', label: 'Cormorant Garamond', family: 'Cormorant Garamond', provider: PROVIDER, category: 'Serif elegante' },
  { value: 'playfair', label: 'Playfair Display', family: 'Playfair Display', provider: PROVIDER, category: 'Serif elegante' },
  { value: 'marcellus', label: 'Marcellus', family: 'Marcellus', provider: PROVIDER, category: 'Serif elegante' },
  { value: 'italiana', label: 'Italiana', family: 'Italiana', provider: PROVIDER, category: 'Serif elegante' },
  { value: 'forum', label: 'Forum', family: 'Forum', provider: PROVIDER, category: 'Serif elegante' },
  { value: 'fraunces', label: 'Fraunces', family: 'Fraunces', provider: PROVIDER, category: 'Serif elegante' },
  { value: 'prata', label: 'Prata', family: 'Prata', provider: PROVIDER, category: 'Serif editorial' },
  { value: 'libre-baskerville', label: 'Libre Baskerville', family: 'Libre Baskerville', provider: PROVIDER, category: 'Serif editorial' },
  { value: 'lora', label: 'Lora', family: 'Lora', provider: PROVIDER, category: 'Serif editorial' },
  { value: 'tenor', label: 'Tenor Sans', family: 'Tenor Sans', provider: PROVIDER, category: 'Sans refinada' },
  { value: 'bricolage', label: 'Bricolage Grotesque', family: 'Bricolage Grotesque', provider: PROVIDER, category: 'Sans expresiva' },
  { value: 'sora', label: 'Sora', family: 'Sora', provider: PROVIDER, category: 'Sans expresiva' },
  { value: 'space-grotesk', label: 'Space Grotesk', family: 'Space Grotesk', provider: PROVIDER, category: 'Sans expresiva' },
  { value: 'montserrat', label: 'Montserrat', family: 'Montserrat', provider: PROVIDER, category: 'Sans estructurada' },
  { value: 'archivo', label: 'Archivo', family: 'Archivo', provider: PROVIDER, category: 'Sans estructurada' },
];

export const BODY_FONT_OPTIONS: BrandingOption<BodyFont>[] = [
  { value: 'dm-sans', label: 'DM Sans', family: 'DM Sans', provider: PROVIDER, category: 'Sans neutral' },
  { value: 'manrope', label: 'Manrope', family: 'Manrope', provider: PROVIDER, category: 'Sans neutral' },
  { value: 'albert-sans', label: 'Albert Sans', family: 'Albert Sans', provider: PROVIDER, category: 'Sans humanista' },
  { value: 'inter-tight', label: 'Inter Tight', family: 'Inter Tight', provider: PROVIDER, category: 'Sans UI' },
  { value: 'inter', label: 'Inter', family: 'Inter', provider: PROVIDER, category: 'Sans UI' },
  { value: 'outfit', label: 'Outfit', family: 'Outfit', provider: PROVIDER, category: 'Sans geométrica' },
  { value: 'plus-jakarta', label: 'Plus Jakarta Sans', family: 'Plus Jakarta Sans', provider: PROVIDER, category: 'Sans UI' },
  { value: 'source-sans', label: 'Source Sans 3', family: 'Source Sans 3', provider: PROVIDER, category: 'Sans humanista' },
  { value: 'work-sans', label: 'Work Sans', family: 'Work Sans', provider: PROVIDER, category: 'Sans geométrica' },
  { value: 'instrument-sans', label: 'Instrument Sans', family: 'Instrument Sans', provider: PROVIDER, category: 'Sans UI' },
  { value: 'public-sans', label: 'Public Sans', family: 'Public Sans', provider: PROVIDER, category: 'Sans institucional' },
  { value: 'nunito-sans', label: 'Nunito Sans', family: 'Nunito Sans', provider: PROVIDER, category: 'Sans amable' },
  { value: 'nunito', label: 'Nunito', family: 'Nunito', provider: PROVIDER, category: 'Sans amable' },
  { value: 'karla', label: 'Karla', family: 'Karla', provider: PROVIDER, category: 'Sans humanista' },
];

export const SURFACE_STYLE_OPTIONS: BrandingOption<SurfaceStyle>[] = [
  { value: 'solid', label: 'Sólido' },
  { value: 'glass', label: 'Cristal (glass)' },
];

export const BACKGROUND_STYLE_OPTIONS: BrandingOption<BackgroundStyle>[] = [
  { value: 'plain', label: 'Plano' },
  { value: 'mesh', label: 'Degradado mesh' },
];

export const GLASS_BLUR_OPTIONS: BrandingOption<GlassBlur>[] = [
  { value: 'subtle', label: 'Sutil' },
  { value: 'regular', label: 'Regular' },
  { value: 'strong', label: 'Fuerte' },
];

const DEFAULT_THEME: BrandingTheme = {
  colorTheme: 'stone',
  accent: '#2D3E50',
  deepAlt: '#232320',
  fontDisplay: 'fraunces',
  fontBody: 'manrope',
  density: 'regular',
  fontScale: 'md',
  cardStyle: 'frame',
  cornerRadius: 'subtle',
  cardBorderColor: 'soft',
  cardBorderWidth: 'thin',
  surfaceStyle: 'solid',
  backgroundStyle: 'plain',
  glassBlur: 'regular',
};

const BRANDING_SUGGESTIONS: Record<string, Omit<BrandingSuggestion, 'industryKey' | 'theme'> & { theme: Required<Omit<BrandingTheme, 'brandingSource' | 'brandingIndustry' | 'brandingLabel' | 'brandingReason' | 'accent2' | 'accent3' | 'fontBaseRem' | 'surfaceStyle' | 'backgroundStyle' | 'glassBlur'>> & Partial<Pick<BrandingTheme, 'accent2' | 'accent3' | 'fontBaseRem' | 'surfaceStyle' | 'backgroundStyle' | 'glassBlur'>> }> = {
  inmobiliaria: {
    key: 'inmobiliaria-premium-editorial',
    label: 'Editorial sobrio',
    paletteName: 'Lino cálido',
    rationale: 'Usa neutros cálidos y serif elegante para comunicar confianza, detalle y una sensación premium.',
    theme: {
      colorTheme: 'lino',
      accent: '#8E6A3D',
      deepAlt: '#2A2620',
      fontDisplay: 'fraunces',
      fontBody: 'plus-jakarta',
      density: 'spacious',
      fontScale: 'md',
      cardStyle: 'frame',
      cornerRadius: 'subtle',
      cardBorderColor: 'gold',
      cardBorderWidth: 'thin',
    },
  },
  restaurante: {
    key: 'restaurante-editorial-vibrante',
    label: 'Cálido apetecible',
    paletteName: 'Crema con acento terracota',
    rationale: 'Combina una base luminosa con acentos cálidos y una serif con carácter para verse acogedor y memorable.',
    theme: {
      colorTheme: 'crema',
      accent: '#A44A2F',
      deepAlt: '#3B241A',
      fontDisplay: 'playfair',
      fontBody: 'manrope',
      density: 'regular',
      fontScale: 'md',
      cardStyle: 'shadow',
      cornerRadius: 'rounded',
      cardBorderColor: 'accent',
      cardBorderWidth: 'thin',
    },
  },
  salud: {
    key: 'salud-limpio-confiable',
    label: 'Limpio confiable',
    paletteName: 'Ocean sereno',
    rationale: 'Los azules sobrios y una tipografía clara ayudan a transmitir limpieza, profesionalismo y calma.',
    theme: {
      colorTheme: 'ocean',
      accent: '#2F6B84',
      deepAlt: '#153547',
      fontDisplay: 'marcellus',
      fontBody: 'source-sans',
      density: 'regular',
      fontScale: 'md',
      cardStyle: 'frame',
      cornerRadius: 'subtle',
      cardBorderColor: 'soft',
      cardBorderWidth: 'thin',
    },
  },
  servicios: {
    key: 'servicios-claro-profesional',
    label: 'Profesional cercano',
    paletteName: 'Slate ejecutivo',
    rationale: 'Un azul grisáceo con sans limpia mantiene el sitio profesional sin verse frío ni distante.',
    theme: {
      colorTheme: 'slate',
      accent: '#385272',
      deepAlt: '#1E2432',
      fontDisplay: 'sora',
      fontBody: 'public-sans',
      density: 'regular',
      fontScale: 'md',
      cardStyle: 'frame',
      cornerRadius: 'subtle',
      cardBorderColor: 'accent',
      cardBorderWidth: 'thin',
    },
  },
  comercio: {
    key: 'comercio-moderno-directo',
    label: 'Moderno comercial',
    paletteName: 'Stone con acento ancla',
    rationale: 'Una base neutra con contraste firme deja que el producto destaque y facilita la lectura del catálogo.',
    theme: {
      colorTheme: 'stone',
      accent: '#2D3E50',
      deepAlt: '#232320',
      fontDisplay: 'space-grotesk',
      fontBody: 'inter-tight',
      density: 'regular',
      fontScale: 'md',
      cardStyle: 'shadow',
      cornerRadius: 'rounded',
      cardBorderColor: 'soft',
      cardBorderWidth: 'thin',
    },
  },
  belleza: {
    key: 'belleza-suave-premium',
    label: 'Suave premium',
    paletteName: 'Crema con acento rosa tierra',
    rationale: 'Tonos suaves con alto contraste y serif refinada proyectan cuidado personal, detalle y una sensación boutique.',
    theme: {
      colorTheme: 'crema',
      accent: '#A65D74',
      deepAlt: '#352431',
      fontDisplay: 'italiana',
      fontBody: 'dm-sans',
      density: 'spacious',
      fontScale: 'md',
      cardStyle: 'shadow',
      cornerRadius: 'rounded',
      cardBorderColor: 'accent',
      cardBorderWidth: 'thin',
    },
  },
  educacion: {
    key: 'educacion-claro-humano',
    label: 'Claro humano',
    paletteName: 'Forest académico',
    rationale: 'Verdes sobrios y tipografías legibles ayudan a explicar oferta educativa y generar cercanía.',
    theme: {
      colorTheme: 'forest',
      accent: '#456A4D',
      deepAlt: '#22382A',
      fontDisplay: 'fraunces',
      fontBody: 'source-sans',
      density: 'regular',
      fontScale: 'md',
      cardStyle: 'frame',
      cornerRadius: 'subtle',
      cardBorderColor: 'soft',
      cardBorderWidth: 'thin',
    },
  },
  fitness: {
    key: 'fitness-energetico-limpio',
    label: 'Enérgico limpio',
    paletteName: 'Carbon atlético',
    rationale: 'Fondos oscuros con acentos vivos y sans contundente proyectan energía, disciplina y movimiento.',
    theme: {
      colorTheme: 'carbon',
      accent: '#A86F2A',
      deepAlt: '#0A0908',
      fontDisplay: 'archivo',
      fontBody: 'outfit',
      density: 'compact',
      fontScale: 'md',
      cardStyle: 'minimal',
      cornerRadius: 'rounded',
      cardBorderColor: 'accent',
      cardBorderWidth: 'regular',
    },
  },
  veterinaria: {
    key: 'veterinaria-calido-confiable',
    label: 'Cálido confiable',
    paletteName: 'Forest amable',
    rationale: 'Verdes suaves y tipografías amigables ayudan a verse cercano sin perder profesionalismo.',
    theme: {
      colorTheme: 'forest',
      accent: '#5B7F56',
      deepAlt: '#263D26',
      fontDisplay: 'fraunces',
      fontBody: 'manrope',
      density: 'regular',
      fontScale: 'md',
      cardStyle: 'shadow',
      cornerRadius: 'rounded',
      cardBorderColor: 'soft',
      cardBorderWidth: 'thin',
    },
  },
  automotriz: {
    key: 'automotriz-solido-tecnico',
    label: 'Sólido técnico',
    paletteName: 'Carbon mecánico',
    rationale: 'Oscuros firmes con sans de alto contraste ayudan a transmitir precisión, confianza y trabajo serio.',
    theme: {
      colorTheme: 'carbon',
      accent: '#C46A2D',
      deepAlt: '#0A0908',
      fontDisplay: 'archivo',
      fontBody: 'inter-tight',
      density: 'compact',
      fontScale: 'md',
      cardStyle: 'minimal',
      cornerRadius: 'subtle',
      cardBorderColor: 'accent',
      cardBorderWidth: 'regular',
    },
  },
  construccion: {
    key: 'construccion-solido-editorial',
    label: 'Sólido editorial',
    paletteName: 'Stone arquitectónico',
    rationale: 'Neutros minerales y serif sobria comunican estructura, oficio y detalle de ejecución.',
    theme: {
      colorTheme: 'stone',
      accent: '#7A5A3B',
      deepAlt: '#2F2B26',
      fontDisplay: 'libre-baskerville',
      fontBody: 'plus-jakarta',
      density: 'spacious',
      fontScale: 'md',
      cardStyle: 'frame',
      cornerRadius: 'none',
      cardBorderColor: 'ink',
      cardBorderWidth: 'thin',
    },
  },
  turismo: {
    key: 'turismo-editorial-escapada',
    label: 'Editorial escapada',
    paletteName: 'Ocean con arena',
    rationale: 'Azules profundos y serif de viaje ayudan a vender experiencia, atmósfera y descubrimiento.',
    theme: {
      colorTheme: 'ocean',
      accent: '#C2844A',
      deepAlt: '#17384A',
      fontDisplay: 'playfair',
      fontBody: 'dm-sans',
      density: 'spacious',
      fontScale: 'md',
      cardStyle: 'shadow',
      cornerRadius: 'rounded',
      cardBorderColor: 'accent',
      cardBorderWidth: 'thin',
    },
  },
  legal: {
    key: 'legal-sobrio-institucional',
    label: 'Sobrio institucional',
    paletteName: 'Slate institucional',
    rationale: 'Azules grisáceos, serif sobria y estructura contenida refuerzan autoridad, orden y credibilidad.',
    theme: {
      colorTheme: 'slate',
      accent: '#324C6B',
      deepAlt: '#182231',
      fontDisplay: 'prata',
      fontBody: 'public-sans',
      density: 'regular',
      fontScale: 'md',
      cardStyle: 'frame',
      cornerRadius: 'none',
      cardBorderColor: 'ink',
      cardBorderWidth: 'thin',
    },
  },
  eventos: {
    key: 'eventos-ligero-emotivo',
    label: 'Emotivo elegante',
    paletteName: 'Crema celebración',
    rationale: 'Una paleta clara con acento festivo y serif elegante ayuda a vender emoción, cuidado y estilo.',
    theme: {
      colorTheme: 'crema',
      accent: '#9E6A86',
      deepAlt: '#302134',
      fontDisplay: 'italiana',
      fontBody: 'outfit',
      density: 'spacious',
      fontScale: 'md',
      cardStyle: 'shadow',
      cornerRadius: 'rounded',
      cardBorderColor: 'accent',
      cardBorderWidth: 'thin',
    },
  },
  software: {
    key: 'software-glass-saas',
    label: 'SaaS glass',
    paletteName: 'Índigo + miel translúcido',
    rationale: 'Superficies de cristal sobre un fondo degradado animado, con índigo y un acento miel, proyectan un producto digital moderno y premium.',
    theme: {
      colorTheme: 'ocean',
      accent: '#4F46E5',
      accent2: '#E0A33E',
      deepAlt: '#1E1B4B',
      fontDisplay: 'sora',
      fontBody: 'nunito',
      density: 'regular',
      fontScale: 'md',
      cardStyle: 'shadow',
      cornerRadius: 'rounded',
      cardBorderColor: 'soft',
      cardBorderWidth: 'thin',
      surfaceStyle: 'glass',
      backgroundStyle: 'mesh',
      glassBlur: 'regular',
    },
  },
  otro: {
    key: 'otro-neutro-flexible',
    label: 'Neutro flexible',
    paletteName: 'Stone versátil',
    rationale: 'Parte de una base neutral y clara que funciona bien para la mayoría de negocios y luego se puede afinar.',
    theme: {
      colorTheme: 'stone',
      accent: '#2D3E50',
      deepAlt: '#232320',
      fontDisplay: 'fraunces',
      fontBody: 'manrope',
      density: 'regular',
      fontScale: 'md',
      cardStyle: 'frame',
      cornerRadius: 'subtle',
      cardBorderColor: 'soft',
      cardBorderWidth: 'thin',
    },
  },
};

const DEFAULT_SUGGESTION = BRANDING_SUGGESTIONS.otro;

export const COLOR_THEME_VALUES = COLOR_THEME_OPTIONS.map((item) => item.value);
export const DISPLAY_FONT_VALUES = DISPLAY_FONT_OPTIONS.map((item) => item.value);
export const BODY_FONT_VALUES = BODY_FONT_OPTIONS.map((item) => item.value);
export const DENSITY_VALUES: Density[] = ['compact', 'regular', 'spacious'];
export const FONT_SCALE_VALUES: FontScale[] = ['sm', 'md', 'lg', 'xl'];
export const CARD_STYLE_VALUES: CardStyle[] = ['shadow', 'frame', 'minimal'];
export const CORNER_RADIUS_VALUES: CornerRadius[] = ['none', 'subtle', 'rounded'];
export const CARD_BORDER_COLOR_VALUES: CardBorderColor[] = ['gold', 'accent', 'ink', 'soft'];
export const CARD_BORDER_WIDTH_VALUES: CardBorderWidth[] = ['thin', 'regular', 'thick'];
export const SURFACE_STYLE_VALUES: SurfaceStyle[] = ['solid', 'glass'];
export const BACKGROUND_STYLE_VALUES: BackgroundStyle[] = ['plain', 'mesh'];
export const GLASS_BLUR_VALUES: GlassBlur[] = ['subtle', 'regular', 'strong'];

export function getColorThemeOption(value: ColorTheme | string | null | undefined) {
  return COLOR_THEME_OPTIONS.find((item) => item.value === value);
}

export function getDisplayFontOption(value: DisplayFont | string | null | undefined) {
  return DISPLAY_FONT_OPTIONS.find((item) => item.value === value);
}

export function getBodyFontOption(value: BodyFont | string | null | undefined) {
  return BODY_FONT_OPTIONS.find((item) => item.value === value);
}

export function isValidHexColor(value: string | null | undefined): value is string {
  return typeof value === 'string' && /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value.trim());
}

export function normalizeHexColor(value: string | null | undefined, fallback: string): string {
  if (!isValidHexColor(value)) return fallback;
  const normalized = value.trim();
  if (normalized.length === 4) {
    return `#${normalized[1]}${normalized[1]}${normalized[2]}${normalized[2]}${normalized[3]}${normalized[3]}`.toUpperCase();
  }
  return normalized.toUpperCase();
}

export function sanitizeBrandingTheme(
  theme: Partial<BrandingTheme> | null | undefined,
  fallback: Partial<BrandingTheme> = DEFAULT_THEME,
): BrandingTheme {
  const base = { ...DEFAULT_THEME, ...fallback };
  const next = theme ?? {};
  return {
    colorTheme: COLOR_THEME_VALUES.includes(next.colorTheme as ColorTheme) ? next.colorTheme as ColorTheme : base.colorTheme,
    accent: normalizeHexColor(next.accent, base.accent!),
    // accent2 es opcional: si no hay valor válido se deja undefined (theme.ts lo deriva del tema).
    accent2: isValidHexColor(next.accent2 ?? base.accent2) ? normalizeHexColor(next.accent2 ?? base.accent2, '#000000') : undefined,
    // accent3 (tercer color de marca) también opcional: undefined si no es hex válido.
    accent3: isValidHexColor(next.accent3 ?? base.accent3) ? normalizeHexColor(next.accent3 ?? base.accent3, '#000000') : undefined,
    deepAlt: normalizeHexColor(next.deepAlt, base.deepAlt!),
    fontDisplay: DISPLAY_FONT_VALUES.includes(next.fontDisplay as DisplayFont) ? next.fontDisplay as DisplayFont : base.fontDisplay,
    fontBody: BODY_FONT_VALUES.includes(next.fontBody as BodyFont) ? next.fontBody as BodyFont : base.fontBody,
    density: DENSITY_VALUES.includes(next.density as Density) ? next.density as Density : base.density,
    fontScale: FONT_SCALE_VALUES.includes(next.fontScale as FontScale) ? next.fontScale as FontScale : base.fontScale,
    // fontBaseRem es opcional: si no es número válido se deja undefined (→ gobierna fontScale).
    fontBaseRem: sanitizeFontBaseRem(next.fontBaseRem ?? base.fontBaseRem),
    cardStyle: CARD_STYLE_VALUES.includes(next.cardStyle as CardStyle) ? next.cardStyle as CardStyle : base.cardStyle,
    cornerRadius: CORNER_RADIUS_VALUES.includes(next.cornerRadius as CornerRadius) ? next.cornerRadius as CornerRadius : base.cornerRadius,
    cardBorderColor: CARD_BORDER_COLOR_VALUES.includes(next.cardBorderColor as CardBorderColor)
      ? next.cardBorderColor as CardBorderColor
      : base.cardBorderColor,
    cardBorderWidth: CARD_BORDER_WIDTH_VALUES.includes(next.cardBorderWidth as CardBorderWidth)
      ? next.cardBorderWidth as CardBorderWidth
      : base.cardBorderWidth,
    surfaceStyle: SURFACE_STYLE_VALUES.includes(next.surfaceStyle as SurfaceStyle) ? next.surfaceStyle as SurfaceStyle : base.surfaceStyle,
    backgroundStyle: BACKGROUND_STYLE_VALUES.includes(next.backgroundStyle as BackgroundStyle) ? next.backgroundStyle as BackgroundStyle : base.backgroundStyle,
    glassBlur: GLASS_BLUR_VALUES.includes(next.glassBlur as GlassBlur) ? next.glassBlur as GlassBlur : base.glassBlur,
    brandingSource: next.brandingSource,
    brandingIndustry: next.brandingIndustry,
    brandingLabel: next.brandingLabel,
    brandingReason: next.brandingReason,
  };
}

export function getBrandingSuggestion(industryKey: string | null | undefined): BrandingSuggestion {
  const suggestion = BRANDING_SUGGESTIONS[industryKey ?? ''] ?? DEFAULT_SUGGESTION;
  return {
    key: suggestion.key,
    industryKey: industryKey ?? null,
    label: suggestion.label,
    paletteName: suggestion.paletteName,
    rationale: suggestion.rationale,
    theme: {
      ...sanitizeBrandingTheme(suggestion.theme, suggestion.theme),
      brandingSource: 'industry-suggestion',
      brandingIndustry: industryKey ?? 'otro',
      brandingLabel: suggestion.label,
      brandingReason: suggestion.rationale,
    },
  };
}

export function hasProtectedBranding(theme: Partial<BrandingTheme> | null | undefined): boolean {
  return theme?.brandingSource === 'user-custom' || theme?.brandingSource === 'ai-refined';
}

// Dirección visual breve por industria para enriquecer los prompts de imagen IA.
// Reutiliza la sugerencia de branding curada (estilo + paleta) sin imponer un hex
// concreto: guía el "mood" de la imagen para que encaje con el giro del negocio.
export function imageStyleHintForIndustry(industryKey: string | null | undefined): string {
  const s = getBrandingSuggestion(industryKey);
  return `Dirección visual coherente con una estética "${s.label}" y una paleta ${s.paletteName}. ` +
    'Imagen sofisticada, sin texto, sin logos y sin marcas de agua.';
}

// Tono/registro de copy por industria — guía el ESTILO del texto generado (no el
// dato), para que el copywriter "suene" como el giro del negocio. El SaaS owner
// puede sobreescribir cada giro desde el panel (industryCopyHints). 'otro' es el
// fallback. Mismas claves que BRANDING_SUGGESTIONS.
const COPY_HINTS: Record<string, string> = {
  inmobiliaria: 'Aspiracional pero confiable: destaca ubicación, estilo de vida y la seguridad de la inversión.',
  restaurante: 'Sensorial y cálido: evoca sabores, ambiente y experiencia; invita a reservar o visitar.',
  salud: 'Cercano y tranquilizador: transmite profesionalismo y empatía; genera confianza y evita tecnicismos.',
  servicios: 'Claro y orientado a resultados: enfatiza fiabilidad, rapidez y trato profesional.',
  comercio: 'Directo y persuasivo: resalta producto, beneficio y oferta; impulsa la acción de compra.',
  belleza: 'Cálido y aspiracional: resalta bienestar, resultados y la experiencia personal del cliente.',
  educacion: 'Inspirador y confiable: enfatiza crecimiento, acompañamiento y los logros del estudiante.',
  fitness: 'Enérgico y motivador: orientado a metas, constancia y transformación.',
  veterinaria: 'Cálido y empático: transmite cuidado y cercanía para la familia y su mascota.',
  automotriz: 'Seguro y técnico-accesible: enfatiza confianza, desempeño y un servicio honesto.',
  construccion: 'Sólido y confiable: enfatiza experiencia, cumplimiento de plazos y calidad de obra.',
  turismo: 'Evocador y emocionante: despierta el deseo de viajar y vivir experiencias memorables.',
  legal: 'Serio y claro: transmite autoridad, discreción y confianza; sin jerga innecesaria.',
  eventos: 'Festivo y cuidado: transmite emoción, atención al detalle y experiencias memorables.',
  software: 'Claro y moderno: enfatiza valor, simplicidad y resultados; evita jerga técnica vacía.',
  otro: 'Profesional, cercano y orientado a beneficios concretos para el cliente.',
};

// Pista de tono de copy por industria (catálogo de código). Se inyecta como
// CONTEXTO en los prompts de texto; el SaaS owner puede sobreescribirla por giro.
export function copyHintForIndustry(industryKey: string | null | undefined): string {
  return COPY_HINTS[industryKey ?? ''] ?? COPY_HINTS.otro;
}

// Giros conocidos (mismas claves que los catálogos). El panel los muestra como
// filas editables pre-rellenadas con su valor por defecto.
export const AI_INDUSTRY_KEYS = Object.keys(COPY_HINTS);

// Mapas completos de defaults por industria — los consume el panel para mostrar
// el catálogo curado como punto de partida editable (no solo placeholder).
export function defaultImageStyleHints(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of AI_INDUSTRY_KEYS) out[k] = imageStyleHintForIndustry(k);
  return out;
}
export function defaultCopyHints(): Record<string, string> {
  return { ...COPY_HINTS };
}
